<template>
  <section class="projection-live" :class="{ embedded: section !== 'all' }" :aria-busy="busy ? 'true' : 'false'">
    <header v-if="section === 'all'" class="projection-header">
      <div>
        <p class="eyebrow">Admisión · Seguimiento comercial</p>
        <h1>Plan de matrículas</h1>
        <p class="muted">Distribución semanal del plan original de matrículas.</p>
      </div>
      <button type="button" class="button secondary" :disabled="busy" @click="reload">{{ loading || awaitingReload ? 'Actualizando…' : 'Actualizar vista' }}</button>
    </header>

    <div v-if="error && section === 'all'" class="notice danger" role="alert">{{ error }}</div>
    <p v-if="loading && section === 'all'" class="notice info" role="status">Cargando los agregados del período. Los datos visibles corresponden a la última respuesta.</p>

    <section v-if="section !== 'analysis'" class="panel upload-panel" aria-label="Carga de archivos Excel">
      <div class="section-heading">
        <div><p class="eyebrow">Fuente de datos</p><h2>Cargar y revisar Excel</h2></div>
        <span class="badge">{{ canImport ? 'Carga autorizada' : 'Solo lectura' }}</span>
      </div>
      <p class="muted">Selecciona el archivo, revisa el resumen y confirma la carga. Nada se importa antes de confirmar.</p>
      <details class="help-details"><summary>Formato del archivo y reglas de carga</summary>
        <p>Las ventas se leen de la <strong>segunda hoja</strong>. Carga archivos separados por año y selecciona un período de ese año. Esta carga conserva las metas existentes; no solicita un archivo de metas.</p>
        <p>Los registros ausentes se conservan; los períodos ausentes mantienen sus datos. Informa el corte real de la descarga, no necesariamente la fecha de hoy ni la última matrícula registrada.</p>
      </details>
      <div class="import-fields">
        <label>Período del catálogo
          <select :value="selectedPeriod" :disabled="busy || !periodOptions.length" @change="selectPeriod($event.target.value)">
            <option disabled value="">Selecciona un período</option>
            <option v-for="option in periodOptions" :key="option.period" :value="option.period">{{ option.period }}</option>
          </select>
        </label>
        <template v-if="canImport">
          <label>Excel de ventas · obligatorio
            <input ref="fileInput" type="file" accept=".xlsx" :disabled="busy" @change="setFile('file', $event)">
            <small>.xlsx · máximo 20 MB por archivo</small>
          </label>
          <label>Datos completos hasta
            <input type="date" :value="coverageThrough" :max="today" :disabled="busy" required @input="setCoverage($event.target.value)">
            <small>Corte inclusive, no posterior a hoy (Chile).</small>
          </label>
        </template>
      </div>
      <p v-if="!canImport" class="notice info">Puedes consultar los resultados. La carga y confirmación requieren permiso de importación.</p>
      <p v-if="payload && payload.demo" class="notice warning">La muestra de demostración está activa. Desactívala antes de importar.</p>
      <template v-if="canImport">
        <label class="reconcile-option">
          <input type="checkbox" :checked="reconcileIds" :disabled="busy" @change="setReconcileIds($event.target.checked)">
          Conciliar IDs renumerados del archivo
        </label>
        <p v-if="reconcileIds" class="muted">Se verificará cada matrícula por código de alumno, carrera y período. Las matrículas anteriores sin coincidencia se conservarán para revisión y se cargará el resto. Las identidades incompletas o ambiguas sí requieren corrección. Al confirmar se guardará un respaldo.</p>
        <div class="actions">
          <button type="button" class="button primary" :disabled="busy || !validInputs || !!(payload && payload.demo)" @click="preparePreview">{{ phase === 'preview' ? 'Revisando archivo…' : 'Generar vista previa' }}</button>
          <button v-if="file || preview" type="button" class="button secondary" :disabled="busy" @click="clearImport">Limpiar selección</button>
          <span class="muted">No modifica datos hasta confirmar.</span>
        </div>
        <p v-if="importError" class="notice danger" role="alert">{{ importError }}</p>
        <p v-if="success" class="notice success" role="status">{{ success }}</p>
        <section v-if="preview" class="preview" aria-label="Resumen de vista previa" aria-live="polite">
          <div class="section-heading"><h3>Revisa antes de confirmar</h3><span class="badge">Corte {{ dateLabel(coverageThrough) }}</span></div>
          <p>Períodos detectados: <strong>{{ (preview.detected_periods || []).join(' · ') || 'No informados' }}</strong>. La carga puede actualizar todos los períodos detectados del año.</p>
          <dl class="count-grid">
            <div v-for="item in previewCounts" :key="item.key"><dt>{{ item.label }}</dt><dd>{{ number(item.value) }}</dd></div>
          </dl>
          <p v-if="preview.reconcile_ids" class="notice info">Conciliación revisada: {{ number(preview.counts && preview.counts.reindexed_ids) }} IDs renumerados. No se han guardado cambios todavía.</p>
          <p class="muted">Las altas y cambios corresponden a registros procesados; no equivalen necesariamente a matrículas incluidas. Las filas excluidas no se suman al segmento.</p>
          <ul v-if="previewWarnings.length" class="warning-list"><li v-for="(message, index) in previewWarnings" :key="index">{{ message }}</li></ul>
          <div class="preview-totals">
            <p v-for="item in previewGroups" :key="item.key"><strong>{{ item.label }}:</strong> {{ number(item.total) }} matrículas · meta Excel {{ number(item.target) }}</p>
          </div>
          <p>Los {{ number(preview.counts && preview.counts.missing_retained) }} registros ausentes se conservarán; no se eliminan automáticamente.</p>
          <p class="muted">Vista previa válida hasta {{ dateTimeLabel(preview.expires_at) }}. Cambiar cualquier dato exige revisarla otra vez.</p>
          <p v-if="previewExpired" class="notice warning">La vista previa venció. Genera una nueva antes de confirmar.</p>
          <button type="button" class="button primary" :disabled="busy || previewExpired || !preview.token" @click="confirmImport">{{ phase === 'confirm' ? 'Confirmando carga…' : 'Confirmar importación' }}</button>
        </section>
      </template>
    </section>

    <template v-if="section !== 'upload'">
    <section aria-label="Planificación Excel por segmento">
      <div class="section-heading analysis-heading"><div><p class="eyebrow">Planificación Excel</p><h2>{{ section === 'all' ? 'Períodos activos' : 'Plan original por semana' }}</h2><p class="muted">Fechas, pesos y metas semanales del plan original.</p></div><span class="badge">{{ selectedPeriod }}</span></div>
      <div class="segment-switch" role="group" aria-label="Segmento">
        <button v-for="item in segments" :key="item.key" type="button" :class="{ selected: segment === item.key }" :aria-pressed="segment === item.key ? 'true' : 'false'" @click="segment = item.key">{{ item.label }}</button>
      </div>
      <p class="scope-note">Análisis por segmento, sin reparto por coordinador ni metas por carrera. Este selector solo cambia el análisis Excel.</p>
      <p v-if="segment === 'postgrado' || segment === 'diplomados'" class="notice info">Magíster/Posgrado y Diplomados se muestran separados, con metas propias. No se consolidan ni se sustituyen datos sin cobertura; una meta ausente no se toma del otro segmento.</p>
      <p v-if="section === 'all' && payload && activeCards.length !== 2" class="notice warning" role="status">El catálogo informa {{ activeCards.length }} períodos activos, en lugar de dos. Se muestran solo los períodos informados, sin crear otros.</p>
      <div v-if="section === 'all'" class="period-grid">
        <button v-for="card in activeCards" :key="card.period" type="button" class="period-card" :class="{ selected: selectedPeriod === card.period }" :disabled="busy" :aria-pressed="selectedPeriod === card.period ? 'true' : 'false'" @click="selectPeriod(card.period)">
          <span class="period-card-heading"><strong>{{ card.period }}</strong><span class="badge">{{ selectedPeriod === card.period ? 'Seleccionado' : 'Activo' }}</span></span>
          <span class="muted">{{ dateLabel(card.start) }} — {{ dateLabel(card.end) }}</span>
          <span class="period-numbers"><strong>{{ number(card.total) }} <small>matrículas</small></strong><span>Meta Excel <b>{{ number(card.target) }}</b></span></span>
          <span class="muted">{{ card.cutoff ? 'Corte: ' + dateLabel(card.cutoff) : 'Corte no informado' }}</span>
        </button>
      </div>
    </section>

    <section class="panel" aria-label="Plan original por semana">
      <div class="section-heading"><h2>Plan original por semana</h2><span class="badge">{{ segmentLabel }} · {{ selectedPeriod || 'Sin período' }}</span></div>
      <p v-if="!original" class="notice info">No hay plan original disponible para este segmento y período.</p>
      <template v-else>
      <p v-if="original.method === 'calendar_days'" class="notice info">Pesos distribuidos por días de calendario por falta de historia utilizable.</p>
      <p v-if="(group.warnings || []).includes('frozen_target_changed')" class="notice warning">La meta congelada difiere de la actual; el plan original se conserva sin recalcular.</p>
      <p v-if="original.initial > 0" class="muted">Cuotas semanales ({{ number(originalTotals.target) }}) + saldo inicial ({{ number(original.initial) }}) = meta original ({{ number(original.target) }}).</p>
      <div class="table-scroll" tabindex="0" role="region" aria-label="Tabla semanal desplazable">
        <table>
          <caption>Plan original de {{ segmentLabel }} · {{ selectedPeriod }}</caption>
          <thead><tr><th scope="col">Semana / fechas</th><th scope="col">Peso %</th><th scope="col">Meta matrículas</th></tr></thead>
          <tbody>
            <tr v-for="(row, index) in original.weeks" :key="row.start" :class="{ 'current-week': isCurrentWeek(row) }" :aria-current="isCurrentWeek(row) ? 'date' : null">
              <th scope="row">S{{ index + 1 }} · {{ dateLabel(row.start) }} — {{ dateLabel(row.end) }} <span v-if="isCurrentWeek(row)" class="current-week-label">Semana actual</span></th>
              <td>{{ number(row.weight * 100, 2) }} %</td><td>{{ number(row.target) }}</td>
            </tr>
          </tbody>
          <tfoot><tr><th scope="row">Total</th><td>{{ number(originalTotals.weight * 100, 2) }} %</td><td>{{ number(originalTotals.target) }}</td></tr></tfoot>
        </table>
      </div>
      </template>
    </section>
    </template>
  </section>
</template>

<script>
import axios from 'axios';
import VueApexCharts from 'vue-apexcharts';

const SEGMENTS = [
  { key: 'advance', label: 'Advance' },
  { key: 'postgrado', label: 'Magíster/Posgrado' },
  { key: 'diplomados', label: 'Diplomados' },
];
const MAX_FILE_SIZE = 20 * 1024 * 1024;
const DAY = 86400000;
const isNumber = value => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));
const stamp = date => Date.parse(`${date}T00:00:00Z`);
const isoDate = timestamp => new Date(timestamp).toISOString().slice(0, 10);

export default {
  name: 'ProjectionLive',
  components: { apexchart: VueApexCharts },
  props: {
    payload: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' },
    section: { type: String, default: 'all', validator: value => ['all', 'upload', 'analysis'].includes(value) },
    selectedSegment: { type: String, default: 'advance' },
  },
  data() {
    return {
      segments: SEGMENTS, segment: 'advance', selectedPeriod: '',
      file: null, coverageThrough: '', preview: null, reconcileIds: false,
      phase: '', importError: '', success: '', awaitingReload: false,
      inputRevision: 0, now: Date.now(), clock: null, disposed: false,
    };
  },
  computed: {
    context() { return (this.payload && this.payload.context) || {}; },
    periodOptions() {
      return (this.context.periods || []).map(option => typeof option === 'string' ? { period: option } : option);
    },
    canImport() { return !!(this.payload && this.payload.can_import); },
    busy() { return this.loading || !!this.phase || this.awaitingReload; },
    today() {
      const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Santiago', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(this.now));
      const get = type => parts.find(part => part.type === type).value;
      return `${get('year')}-${get('month')}-${get('day')}`;
    },
    validInputs() {
      return !!this.file && !this.fileError(this.file)
        && this.periodOptions.some(option => option.period === this.selectedPeriod)
        && /^\d{4}-\d{2}-\d{2}$/.test(this.coverageThrough)
        && Number.isFinite(stamp(this.coverageThrough))
        && isoDate(stamp(this.coverageThrough)) === this.coverageThrough
        && this.coverageThrough <= this.today;
    },
    previewExpired() {
      const expires = this.preview && Date.parse(this.preview.expires_at);
      return !Number.isFinite(expires) || expires <= this.now;
    },
    previewCounts() {
      const counts = (this.preview && this.preview.counts) || {};
      return [['input_rows', 'Filas leídas'], ['inserted', 'Altas'], ['updated', 'Cambios'], ['unchanged', 'Sin cambios'], ['excluded_rows', 'Excluidas'], ['missing_retained', 'Ausentes conservados'], ['duplicate_rows', 'Duplicadas']]
        .map(([key, label]) => ({ key, label, value: counts[key] }));
    },
    previewGroups() {
      const groups = (this.preview && this.preview.snapshot && this.preview.snapshot.groups) || {};
      return SEGMENTS.map(item => ({ ...item, total: groups[item.key] && groups[item.key].total, target: groups[item.key] && groups[item.key].target }));
    },
    previewWarnings() {
      const warnings = (this.preview && this.preview.warnings) || {};
      const labels = {
        missing_retained: 'Registros ausentes conservados', other_source_year: 'Filas de otro año de origen',
        unconfigured_period: 'Filas con período no habilitado', unclassified_rows: 'Filas sin clasificación de segmento',
      };
      if (Array.isArray(warnings)) return [...new Set(warnings.map(code => this.warningLabel(code)))];
      return Object.keys(warnings).filter(key => Number(warnings[key]) > 0)
        .map(key => `${labels[key] || 'Filas que requieren revisión'}: ${this.number(warnings[key])}.`);
    },
    excel() {
      if (!this.payload || this.context.period !== this.selectedPeriod) return null;
      return this.payload.excel || null;
    },
    planning() {
      const planning = this.payload && this.payload.planning;
      return planning && planning.period === this.selectedPeriod ? planning : {};
    },
    group() { return (this.planning.groups && this.planning.groups[this.segment]) || {}; },
    original() { return this.group.original || null; },
    originalTotals() {
      return ((this.original && this.original.weeks) || []).reduce((totals, week) => ({
        weight: totals.weight + Number(week.weight),
        target: totals.target + Number(week.target),
      }), { weight: 0, target: 0 });
    },
    recovery() { return this.group.recovery || null; },
    yoy() { return this.group.yoy || {}; },
    cutoff() { return (this.recovery && this.recovery.cutoff) || (this.excel && this.excel.coverage_through) || null; },
    dates() {
      const group = this.excel && this.excel.groups && this.excel.groups[this.segment];
      return group && group.dates ? group.dates : null;
    },
    segmentLabel() { return SEGMENTS.find(item => item.key === this.segment).label; },
    nextRecovery() { return this.recovery && this.recovery.weeks && this.recovery.weeks[0]; },
    activeCards() {
      const summaries = (this.payload && this.payload.active_summaries) || [];
      return (this.context.active_periods || []).map(active => {
        const summary = summaries.find(item => item.period === active.period) || {};
        // Legacy team summaries only contain Advance and Magíster. For Diplomados,
        // use the selected Excel only; never borrow another segment/period's totals.
        const selectedGroup = this.segment === 'diplomados' && this.excel && active.period === this.selectedPeriod
          ? this.excel.groups && this.excel.groups[this.segment] : null;
        const group = selectedGroup || (summary.groups && summary.groups[this.segment]) || {};
        return { ...active, total: group.total, target: group.target,
          cutoff: summary.coverage_through || (this.excel && active.period === this.selectedPeriod ? this.excel.coverage_through : null) };
      });
    },
    planningWarnings() { return [...new Set((this.group.warnings || []).map(code => this.warningLabel(code)))]; },
    weeklyRows() {
      if (!this.original) return [];
      return (this.original.weeks || []).map(week => {
        const covered = !!this.cutoff && this.dates !== null && week.start <= this.cutoff;
        const through = covered ? (week.end < this.cutoff ? week.end : this.cutoff) : null;
        const actual = covered ? this.countDates(through, week.start) : null;
        const actualCumulative = covered ? this.countDates(through) : null;
        // Recovery's first bucket can be a remainder of an original week.
        const recoveryWeeks = this.recovery ? (this.recovery.weeks || []).filter(item => item.start >= week.start && item.end <= week.end) : [];
        return { ...week, actual, actualCumulative, through,
          partial: covered && through < week.end,
          gap: actualCumulative !== null && isNumber(week.cumulative) ? actualCumulative - Number(week.cumulative) : null,
          recoveryTarget: this.recovery ? recoveryWeeks.reduce((sum, item) => sum + Number(item.target), 0) : null,
          recoveryStart: recoveryWeeks.length ? recoveryWeeks[0].start : null };
      });
    },
    trajectorySeries() {
      const original = this.weeklyRows.map(row => ({ x: stamp(row.end), y: isNumber(row.cumulative) ? Number(row.cumulative) : null }));
      const actual = this.weeklyRows.map(row => ({ x: stamp(row.through || row.end), y: row.actualCumulative }));
      const recovery = [];
      if (this.recovery && isNumber(this.recovery.actual)) {
        let cumulative = Number(this.recovery.actual);
        recovery.push({ x: stamp(this.recovery.cutoff), y: cumulative });
        if (!actual.some(point => point.x === stamp(this.recovery.cutoff) && point.y !== null)) {
          actual.push({ x: stamp(this.recovery.cutoff), y: cumulative });
          actual.sort((a, b) => a.x - b.x);
        }
        (this.recovery.weeks || []).filter(week => week.start > this.recovery.cutoff).forEach(week => {
          cumulative += Number(week.target);
          recovery.push({ x: stamp(week.end), y: cumulative });
        });
      }
      return [{ name: 'Original congelado · cierre semanal', data: original }, { name: 'Real · hasta el corte', data: actual }, { name: 'Recuperación requerida · no pronóstico', data: recovery }];
    },
    trajectoryOptions() {
      return { ...this.baseChartOptions(), colors: ['#697b93', '#007f76', '#bd650d'],
        stroke: { width: [3, 3, 3], curve: 'straight', dashArray: [5, 0, 7] },
        xaxis: { type: 'datetime', labels: { datetimeUTC: true, format: 'dd/MM/yy' } },
        yaxis: { min: 0, title: { text: 'Matrículas acumuladas' }, labels: { formatter: value => this.number(value) } },
        annotations: { xaxis: this.cutoff ? [{ x: stamp(this.cutoff), borderColor: '#007f76', label: { text: `Corte ${this.dateLabel(this.cutoff)}`, style: { color: '#fff', background: '#007f76' } } }] : [] },
        tooltip: { shared: false, intersect: false, x: { formatter: value => {
          const date = isoDate(value);
          return `${this.dateLabel(date)}${date === this.cutoff ? ' · corte real (no cierre semanal si es parcial)' : ' · cierre de tramo'}`;
        } }, y: { formatter: value => this.number(value) } } };
    },
    yoyRows() { return this.yoy.rows || []; },
    yoySeries() {
      return [{ name: `${this.selectedPeriod} · % meta propia`, data: this.yoyRows.map(row => isNumber(row.actual_pct) ? Number(row.actual_pct) : null) },
        { name: `${this.yoy.previous_period || 'Año anterior'} · % meta propia`, data: this.yoyRows.map(row => isNumber(row.previous_pct) ? Number(row.previous_pct) : null) }];
    },
    hasYoyValues() { return this.yoySeries.some(series => series.data.some(value => value !== null)); },
    previousCalendar() {
      const option = this.periodOptions.find(item => item.period === this.yoy.previous_period);
      if (option && option.start && option.end) return option;
      const source = this.original && (this.original.sources || []).find(item => item.period === this.yoy.previous_period);
      return source ? source.calendar : null;
    },
    previousWeeks() {
      if (!this.previousCalendar) return [];
      const start = stamp(this.previousCalendar.start);
      const end = stamp(this.previousCalendar.end);
      const monday = start - ((new Date(start).getUTCDay() + 6) % 7) * DAY;
      const weeks = [];
      for (let cursor = monday; cursor <= end; cursor += 7 * DAY) {
        weeks.push({ start: isoDate(Math.max(start, cursor)), end: isoDate(Math.min(end, cursor + 6 * DAY)) });
      }
      return weeks;
    },
    yoyOptions() {
      return { ...this.baseChartOptions(), colors: ['#007f76', '#7860a8'], stroke: { width: 3, curve: 'straight', dashArray: [0, 5] },
        xaxis: { type: 'category', categories: this.yoyRows.map((row, index) => `S${index + 1} · ${this.dateLabel(row.start)}–${this.dateLabel(row.end)}`),
          labels: { rotate: -45, hideOverlappingLabels: true, trim: true, maxHeight: 95 }, tickAmount: Math.min(8, this.yoyRows.length) },
        yaxis: { min: 0, title: { text: '% de la meta de cada año' }, labels: { formatter: value => `${this.number(value, 1)} %` } },
        tooltip: { shared: true, intersect: false, x: { formatter: (value, options) => this.yoyDateLabel(options && options.dataPointIndex) },
          y: { formatter: value => isNumber(value) ? `${this.number(value, 1)} %` : 'Sin cobertura o meta válida' } } };
    },
  },
  watch: {
    selectedSegment: { immediate: true, handler(value) {
      if (SEGMENTS.some(item => item.key === value)) this.segment = value;
    } },
    phase(value) { this.$emit('import-busy', !!value); },
    'context.period': { immediate: true, handler(value) {
      if (value && value !== this.selectedPeriod) { this.selectedPeriod = value; this.invalidatePreview(); }
    } },
    payload() {
      if (this.awaitingReload && this.success) this.success = 'Excel guardado y datos de la vista actualizados.';
      this.awaitingReload = false;
    },
    loading(value, previous) { if (previous && !value) this.awaitingReload = false; },
    error(value) { if (value) { this.awaitingReload = false; this.success = ''; } },
    canImport(value) { if (!value) this.clearImport(); },
  },
  mounted() { this.clock = setInterval(() => { this.now = Date.now(); }, 15000); },
  beforeDestroy() { this.disposed = true; this.inputRevision++; clearInterval(this.clock); },
  methods: {
    isCurrentWeek(row) { return row.start <= this.today && this.today <= row.end; },
    number(value, digits = 0) {
      return isNumber(value) ? Number(value).toLocaleString('es-CL', { maximumFractionDigits: digits, minimumFractionDigits: digits }) : '—';
    },
    signed(value, digits = 0) { return isNumber(value) ? `${Number(value) > 0 ? '+' : ''}${this.number(value, digits)}` : '—'; },
    dateLabel(value) {
      if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return 'No informado';
      return `${value.slice(8, 10)}/${value.slice(5, 7)}/${value.slice(0, 4)}`;
    },
    dateTimeLabel(value) {
      return Number.isFinite(Date.parse(value)) ? new Date(value).toLocaleString('es-CL', { timeZone: 'America/Santiago' }) + ' (Chile)' : 'No informado';
    },
    fileError(file) {
      if (!file) return 'Selecciona el Excel de matrículas.';
      if (!/\.xlsx$/i.test(file.name)) return 'Solo se admiten archivos .xlsx.';
      if (file.size > MAX_FILE_SIZE) return 'Cada archivo debe pesar como máximo 20 MB.';
      if (!file.size) return 'El archivo está vacío.';
      return '';
    },
    invalidatePreview() { this.inputRevision++; this.preview = null; this.importError = ''; this.success = ''; },
    setFile(field, event) {
      this.invalidatePreview();
      this.reconcileIds = false;
      const file = event.target.files && event.target.files[0];
      const error = file ? this.fileError(file) : '';
      this[field] = error ? null : (file || null);
      if (error) { event.target.value = ''; this.importError = error; }
    },
    setCoverage(value) { this.invalidatePreview(); this.coverageThrough = value; },
    setReconcileIds(value) {
      if (this.busy) return;
      this.invalidatePreview(); this.reconcileIds = !!value;
    },
    selectPeriod(value) {
      if (this.busy || !this.periodOptions.some(option => option.period === value) || value === this.selectedPeriod) return;
      this.invalidatePreview(); this.reconcileIds = false; this.selectedPeriod = value; this.awaitingReload = true;
      this.$emit('period', value);
    },
    reload() {
      if (this.busy) return;
      this.awaitingReload = true;
      this.$emit('reload');
    },
    clearImport() {
      this.invalidatePreview(); this.file = null; this.coverageThrough = ''; this.reconcileIds = false;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    async preparePreview() {
      if (this.busy || !this.canImport || !this.validInputs || (this.payload && this.payload.demo)) return;
      this.invalidatePreview();
      const revision = this.inputRevision;
      const form = new FormData();
      form.append('file', this.file);
      form.append('period', this.selectedPeriod);
      form.append('coverage_through', this.coverageThrough);
      if (this.reconcileIds) form.append('reconcile_ids', '1');
      this.phase = 'preview';
      try {
        const response = await axios.post('/api/dashboard/proyeccion/importar/preview', form);
        if (this.disposed || revision !== this.inputRevision) return;
        this.now = Date.now();
        if (!response.data || !response.data.token) throw new Error('invalid_preview');
        this.preview = response.data;
      } catch (error) {
        if (!this.disposed && revision === this.inputRevision) this.importError = this.requestError(error, 'No se pudo generar la vista previa. No se importaron datos.');
      } finally { if (!this.disposed) this.phase = ''; }
    },
    async confirmImport() {
      this.now = Date.now();
      if (this.busy || !this.canImport || !this.preview || !this.preview.token || this.previewExpired || (this.payload && this.payload.demo)) return;
      const token = this.preview.token;
      this.phase = 'confirm'; this.importError = ''; this.success = '';
      try {
        const response = await axios.post('/api/dashboard/proyeccion/importar/confirmar', { token });
        if (this.disposed) return;
        const snapshot = response.data && response.data.snapshot;
        const imported = {
          period: (snapshot && snapshot.period) || this.selectedPeriod,
          revision: snapshot && snapshot.revision,
          coverage_through: (snapshot && snapshot.coverage_through) || this.coverageThrough,
        };
        this.clearImport();
        this.success = 'Excel guardado. Actualizando indicadores, gráficos y tablas…';
        this.awaitingReload = true;
        if (this.$listeners.imported) this.$emit('imported', imported);
        else this.$emit('reload');
      } catch (error) {
        if (!this.disposed) {
          this.preview = null;
          this.importError = this.requestError(error, 'No se pudo verificar la confirmación. Actualiza la vista antes de repetir la carga.') + ' Genera una nueva vista previa para volver a confirmar.';
        }
      } finally { if (!this.disposed) this.phase = ''; }
    },
    requestError(error, fallback) {
      const response = error.response || {};
      if (response.status === 401 || response.status === 419) return 'La sesión venció. Vuelve a iniciar sesión antes de continuar.';
      if (response.status === 403) return 'No tienes permiso para realizar esta importación.';
      if (response.status === 413) return 'El servidor rechazó el tamaño de la carga. Revisa el límite de 20 MB por archivo.';
      if (response.status === 429) return 'Hay demasiadas solicitudes. Espera un momento antes de reintentar.';
      const data = response.data || {};
      // Validation messages can be in English; provide actionable Spanish field labels.
      if (data.errors) {
        const labels = { file: 'Revisa el Excel de ventas (.xlsx, hasta 20 MB).', period: 'Selecciona un período habilitado en el catálogo.', coverage_through: 'Indica una fecha de corte válida, no posterior a hoy.', token: 'La vista previa no es válida o venció.' };
        return Object.keys(data.errors).map(key => labels[key] || 'Revisa los datos de la solicitud.').join(' ');
      }
      return typeof data.message === 'string' && response.status >= 400 && response.status < 500
        ? data.message.replace(/preview/gi, 'vista previa') : fallback;
    },
    countDates(through, from = null) {
      return Object.keys(this.dates || {}).reduce((sum, date) => date <= through && (!from || date >= from) ? sum + Number(this.dates[date]) : sum, 0);
    },
    baseChartOptions() {
      return { chart: { toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: false }, fontFamily: 'inherit', foreColor: '#52627a' },
        dataLabels: { enabled: false }, markers: { size: 3, hover: { size: 5 } },
        grid: { borderColor: '#e8edf3', strokeDashArray: 3 }, legend: { position: 'bottom', horizontalAlign: 'left', fontSize: '12px' },
        noData: { text: 'Sin datos disponibles' } };
    },
    yoyDateLabel(index) {
      const row = this.yoyRows[index];
      if (!row) return 'Sin fechas disponibles';
      const covered = row.actual !== null && row.actual !== undefined && this.cutoff;
      const through = covered && this.cutoff < row.end ? this.cutoff : row.end;
      const current = `S${index + 1} · actual ${this.dateLabel(row.start)}–${this.dateLabel(through)}${through < row.end ? ' (corte parcial)' : ''}`;
      const old = this.previousWeeks[index];
      if (!this.previousCalendar) return `${current} · fechas equivalentes anteriores no informadas`;
      let oldThrough = old ? old.end : this.previousCalendar.end;
      if (through < row.end && old) {
        oldThrough = isoDate(Math.min(stamp(oldThrough), stamp(old.start) + stamp(through) - stamp(row.start)));
      } else if (index === this.yoyRows.length - 1) oldThrough = this.previousCalendar.end;
      return `${current} · anterior ${old ? this.dateLabel(old.start) + '–' : 'cierre '}${this.dateLabel(oldThrough)}`;
    },
    warningLabel(code) {
      const [key, period] = String(code).split(':');
      const labels = {
        missing_snapshot: 'Falta el Excel de este segmento; no hay observaciones disponibles.',
        missing_excel_target: 'Falta una meta de origen Excel. No se admite una meta manual local.',
        missing_coverage: 'Falta declarar el corte de cobertura; el real y la recuperación no están disponibles.',
        frozen_target_changed: 'La meta del Excel cambió; el plan original y su recuperación conservan la meta congelada. La comparación interanual usa la meta Excel vigente.',
        frozen_calendar_changed: 'El calendario vigente difiere del congelado. La tabla y la recuperación mantienen las fechas del plan original.',
        fallback_calendar_days: 'Sin históricos utilizables al crear el plan: reparto original por días de calendario.',
        recovery_fallback_calendar_days: 'Los tramos restantes no tienen peso histórico; la recuperación se reparte por días disponibles.',
        initial_unconfirmed: 'Saldo previo a la apertura no confirmado. Si era desconocido, el servicio usó cero solo para distribuir el plan, no como observación real.',
        initial_exceeds_target: 'Las matrículas anticipadas superaban la meta al congelar el plan.',
        partial_cutoff: 'El corte está dentro de una semana. La venta real es parcial y la recuperación comienza al día siguiente.',
        missing_previous_snapshot: 'No hay Excel del año anterior para comparar.',
        missing_previous_excel_target: 'El año anterior no tiene una meta de origen Excel; su porcentaje no está disponible.',
        zero_previous_excel_target: 'La meta del año anterior es cero; no se calcula su porcentaje.',
        zero_excel_target: 'La meta Excel actual es cero; no se calcula su porcentaje.',
        previous_coverage_provisional: 'La cobertura anterior es provisional: se asumió el cierre de su calendario al no existir corte declarado.',
        missing_previous_coverage: 'El histórico no tiene corte de cobertura declarado.',
        incomplete_previous_coverage: 'La cobertura del año anterior está incompleta; solo se muestran observaciones respaldadas.',
      };
      if (key === 'incomplete_history') return `El histórico ${period || ''} estaba incompleto y no se usó para distribuir el plan original.`;
      if (key === 'provisional_history') return `El histórico ${period || ''} se usó de forma provisional por no tener cobertura declarada.`;
      return labels[key] || 'La fuente informa una advertencia adicional. Revisa la cobertura y las metas antes de interpretar los resultados.';
    },
  },
};
</script>

<style scoped>
.reconcile-option { display: flex; align-items: center; gap: 8px; font-size: 12px; margin: 12px 0; cursor: pointer; }
.reconcile-option input { width: 16px; height: 16px; margin: 0; }
.projection-live .current-week > th, .projection-live .current-week > td { background: #e9f6f3; color: #12665e; font-weight: 700; }
.projection-live .current-week > th { box-shadow: inset 4px 0 #0d9488; }
.current-week-label { display: inline-block; padding: 3px 8px; margin: 4px 0 4px 8px; border-radius: 6px; background: #12665e; color: #fff; font-size: 11px; white-space: nowrap; }
.projection-live { --ink: #203047; --muted: #63738a; --line: #e1e8f0; --teal: #007f76; color: var(--ink); background: #f4f7fb; padding: clamp(14px, 2.5vw, 32px); border-radius: 18px; font-size: 14px; line-height: 1.6; }
.projection-live.embedded { --ink: #24324a; --muted: #65738b; --teal: #6254db; background: transparent; padding: 0; border-radius: 0; }
.projection-live.embedded .panel { border-radius: 12px; box-shadow: none; }
.projection-live.embedded .analysis-heading { margin-top: 32px; padding-top: 24px; border-top: 1px solid var(--line); }
.help-details { font-size: 12px; color: var(--muted); margin-bottom: 16px; }
.help-details summary { cursor: pointer; color: var(--teal); font-weight: 600; padding: 6px 0; }
.help-details p { margin-top: 10px; }
.help-details summary:focus-visible { outline: 3px solid #9694ef; outline-offset: 3px; }
@media(min-width:1200px) { .embedded .import-fields { grid-template-columns: minmax(140px,.7fr) minmax(0,1.6fr) minmax(170px,.8fr); gap: 14px; } }
.projection-live * { box-sizing: border-box; }
.projection-live h1, .projection-live h2, .projection-live h3, .projection-live p { margin: 0 0 12px; }
.projection-live h1 { font-size: clamp(25px, 3vw, 34px); font-weight: 750; letter-spacing: -.7px; }
.projection-live h2 { font-size: 20px; font-weight: 700; line-height: 1.35; }
.projection-live h3 { font-size: 17px; font-weight: 700; }
.eyebrow { color: var(--teal); text-transform: uppercase; letter-spacing: 1.5px; font-size: 11px; font-weight: 800; }
.muted, .scope-note { color: var(--muted); }
.scope-note { font-size: 12px; margin-top: 12px !important; }
.projection-header, .section-heading { display: flex; align-items: center; justify-content: space-between; gap: 18px; flex-wrap: wrap; margin-bottom: 18px; }
.projection-header { align-items: flex-start; }
.section-heading h2, .section-heading h3, .section-heading .eyebrow { margin-bottom: 4px; }
.panel { margin: 24px 0; padding: clamp(16px, 2vw, 26px); border: 1px solid var(--line); border-radius: 16px; background: #fff; box-shadow: 0 5px 20px rgba(32,48,71,.035); min-width: 0; }
.upload-panel { border-top: 3px solid var(--teal); }
.badge { display: inline-block; background: #edf3f8; color: #4b6079; padding: 5px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; white-space: normal; line-height: 1.5; }
.button { border: 1px solid transparent; border-radius: 9px; padding: 10px 17px; font: inherit; font-weight: 700; cursor: pointer; transition: background .15s; }
.primary { background: var(--teal); color: #fff; }
.primary:hover:not(:disabled) { background: #00665f; }
.secondary { border-color: #cad5e2; background: #fff; color: var(--ink); }
.secondary:hover:not(:disabled) { background: #f0f5f9; }
.projection-live button:disabled, .projection-live input:disabled, .projection-live select:disabled { opacity: .55; cursor: not-allowed; }
.projection-live button:focus-visible, .projection-live input:focus-visible, .projection-live select:focus-visible, .table-scroll:focus-visible { outline: 3px solid #268eaa; outline-offset: 3px; }
.import-fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; margin: 20px 0; }
.import-fields label { display: flex; flex-direction: column; gap: 7px; font-size: 12px; font-weight: 700; margin: 0; min-width: 0; }
.import-fields input, .import-fields select { border: 1px solid #cbd6e2; border-radius: 8px; background: #fff; padding: 10px; min-height: 44px; width: 100%; min-width: 0; color: var(--ink); font: inherit; font-size: 14px; }
.import-fields input[type=file] { font-size: 12px; }
.import-fields small { color: var(--muted); font-weight: 400; }
.actions { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; }
.actions .muted { font-size: 12px; }
.notice { border: 1px solid; padding: 12px 16px; border-radius: 9px; margin: 14px 0 !important; overflow-wrap: anywhere; }
.info { background: #edf6fc; border-color: #cfe4f3; color: #235673; }
.warning { background: #fff8e9; border-color: #f1dfad; color: #765215; }
.danger { background: #fff0f0; border-color: #f3caca; color: #9c3030; }
.success { background: #edf8f2; border-color: #c4e8d4; color: #246847; }
.preview { margin-top: 22px; border: 1px solid #c2dfd9; padding: 20px; border-radius: 12px; background: #f8fcfb; }
.count-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(110px, 1fr)); gap: 12px; margin: 18px 0; }
.count-grid div { background: #fff; border: 1px solid var(--line); padding: 12px; border-radius: 9px; }
.count-grid dt { font-size: 11px; color: var(--muted); font-weight: 600; }
.count-grid dd { font-size: 24px; font-weight: 750; margin: 3px 0 0; }
.preview-totals { display: flex; flex-wrap: wrap; gap: 10px 28px; }
.warning-list { padding: 14px 16px 14px 33px; margin: 14px 0; border-radius: 9px; background: #fff9ed; color: #765215; font-size: 12px; }
.warning-list li + li { margin-top: 6px; }
.segment-switch { display: inline-flex; flex-wrap: wrap; background: #e8eef5; padding: 5px; border-radius: 11px; gap: 4px; }
.segment-switch button { background: transparent; border: 0; border-radius: 8px; padding: 9px 18px; font: inherit; font-weight: 650; color: #53667e; cursor: pointer; }
.segment-switch button.selected { background: #fff; color: var(--teal); box-shadow: 0 2px 5px #20304712; }
.period-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; margin: 16px 0 24px; }
.period-card { display: flex; flex-direction: column; gap: 9px; padding: 20px; background: #fff; color: var(--ink); border: 2px solid var(--line); border-radius: 14px; text-align: left; font: inherit; cursor: pointer; min-width: 0; }
.period-card.selected { border-color: var(--teal); background: #f4fbf9; }
.period-card-heading, .period-numbers { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; }
.period-card-heading > strong { font-size: 21px; }
.period-card .muted { font-size: 12px; }
.period-numbers > strong { font-size: 30px; letter-spacing: -.7px; }
.period-numbers small { font-size: 12px; font-weight: 500; letter-spacing: 0; }
.kpi-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; margin: 22px 0; }
.kpi { border: 1px solid var(--line); border-radius: 12px; padding: 17px; display: flex; flex-direction: column; gap: 9px; }
.kpi > span { color: var(--muted); font-size: 12px; font-weight: 650; }
.kpi > strong { font-size: clamp(26px, 3vw, 36px); line-height: 1.2; letter-spacing: -.7px; }
.kpi small { color: var(--muted); font-size: 11px; }
.kpi.emphasis { background: #edf8f5; border-color: #c6e2da; }
.kpi.emphasis > strong { color: var(--teal); }
.empty-state { padding: 25px; border: 1px dashed #cdd9e5; border-radius: 12px; background: #f9fbfd; color: var(--muted); text-align: center; margin: 16px 0; }
.table-scroll { overflow-x: auto; border: 1px solid var(--line); border-radius: 10px; }
.table-scroll table { border-collapse: collapse; width: 100%; min-width: 920px; font-size: 12px; }
.table-scroll caption { caption-side: top; padding: 12px 15px; color: var(--muted); text-align: left; }
.table-scroll th, .table-scroll td { padding: 12px 14px; border-bottom: 1px solid #e9eef4; text-align: right; font-variant-numeric: tabular-nums; }
.table-scroll thead th { background: #f1f5f9; color: #52647b; font-size: 11px; }
.table-scroll th:first-child { text-align: left; }
.table-scroll tbody th { font-weight: 500; }
.table-scroll small { display: block; font-size: 10px; }
.table-scroll tbody tr:last-child > * { border-bottom: 0; }
.table-scroll tbody tr:hover { background: #f8fafc; }
.partial-row { background: #fff9ed; }
.partial-label { color: #896117; font-weight: 700; }
.deficit { color: #b44337; font-weight: 700; }
.source-list { list-style: none; padding: 0; margin: 15px 0 0; display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px; }
.source-list li { display: flex; flex-direction: column; border: 1px solid var(--line); border-radius: 10px; padding: 14px; gap: 4px; }
.source-list span { color: var(--muted); font-size: 12px; }
.source-list .provisional { color: #896117; font-weight: 700; }
@media (max-width: 1050px) { .kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 640px) {
  .projection-live { padding: 12px; border-radius: 10px; }
  .panel { padding: 16px; margin: 18px 0; }
  .import-fields, .period-grid { grid-template-columns: minmax(0, 1fr); }
  .kpi-grid { gap: 9px; }
  .kpi { padding: 12px; }
  .preview { padding: 13px; }
  .actions .button { width: 100%; }
  .section-heading { gap: 8px; }
  .segment-switch { display: flex; }
  .segment-switch button { flex: 1; padding: 9px 10px; }
}
@media (prefers-reduced-motion: reduce) { .button { transition: none; } }
</style>